<template>
  <v-container>
    <v-row class="mb-4">
      <v-col cols="12">
        <h1>Timesheet Review</h1>
        <p class="text-body1 text-medium-emphasis">Entries waiting for approval across all gigs</p>
      </v-col>
    </v-row>

    <v-alert v-if="store.error" type="error" closable class="mb-4" @click:close="store.error = null">
      {{ store.error }}
    </v-alert>

    <v-alert v-if="stale.length" type="warning" variant="tonal" class="mb-4">
      {{ stale.length }} {{ stale.length === 1 ? 'person has' : 'people have' }} been clocked in for over 16 hours —
      open the gig to close out their entry.
    </v-alert>

    <v-card>
      <v-card-text v-if="pending.length === 0" class="text-center py-12 text-medium-emphasis">
        Nothing waiting for review
      </v-card-text>
      <v-table v-else>
        <thead>
          <tr>
            <th><v-checkbox-btn :model-value="allSelected" :indeterminate="someSelected && !allSelected" @update:model-value="toggleAll" /></th>
            <th>Person</th>
            <th>In</th>
            <th>Out</th>
            <th class="text-right">Hours</th>
            <th>Gig</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="e in pending" :key="e.id">
            <td><v-checkbox-btn v-model="selected" :value="e.id" :disabled="cannotReviewOwn(e)" /></td>
            <td>{{ e.personName ?? e.personId }}</td>
            <td>{{ formatDateTime(e.clockIn) }}</td>
            <td>{{ e.clockOut ? formatDateTime(e.clockOut) : '—' }}</td>
            <td class="text-right">{{ entryHours(e).toFixed(2) }}<span v-if="e.breakMinutes" class="text-caption text-medium-emphasis"> ({{ e.breakMinutes }}m break)</span></td>
            <td>
              <router-link :to="{ name: 'GigDetail', params: { gigId: e.gigId }, query: { org: e.orgId } }">Open gig</router-link>
            </td>
          </tr>
        </tbody>
      </v-table>
      <v-card-actions v-if="pending.length">
        <v-spacer />
        <v-btn color="success" variant="flat" :disabled="selected.length === 0" :loading="approving" @click="approveSelected">
          Approve {{ selected.length || '' }} selected
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue';
import type { TimesheetEntry } from '@gigdriver/domain';
import { entryHours, needsAttention } from '@gigdriver/domain';
import { useTimesheetsStore } from '../stores/timesheets';
import { useOrganizationStore } from '../stores/organization';
import { useAuthStore } from '../stores/auth';

const store = useTimesheetsStore();
const orgStore = useOrganizationStore();
const authStore = useAuthStore();

// Staff can't read the org-wide queue (rules), so don't even subscribe.
watch(() => orgStore.orgId, id => id && !orgStore.isStaff && store.watchQueue(id), { immediate: true });
onUnmounted(() => store.stopQueueWatch());

const pending = computed(() => store.queueEntries.filter(e => e.status === 'PENDING'));
const stale = computed(() => store.queueEntries.filter(e => needsAttention(e, new Date())));

const selected = ref<string[]>([]);
const approving = ref(false);

function cannotReviewOwn(e: TimesheetEntry): boolean {
  return orgStore.myRole === 'coordinator' && e.userId === authStore.currentUser?.uid;
}
const selectable = computed(() => pending.value.filter(e => !cannotReviewOwn(e)).map(e => e.id));
const allSelected = computed(() => selectable.value.length > 0 && selectable.value.every(id => selected.value.includes(id)));
const someSelected = computed(() => selected.value.length > 0);
function toggleAll(on: boolean | null) {
  selected.value = on ? [...selectable.value] : [];
}

// One callable per entry; failures don't stop the rest, and whatever failed
// stays selected so it can be retried.
async function approveSelected() {
  approving.value = true;
  const failed: string[] = [];
  for (const id of selected.value) {
    const e = pending.value.find(x => x.id === id);
    if (!e) continue;
    try {
      await store.reviewEntry({ orgId: e.orgId, gigId: e.gigId, entryId: e.id, decision: 'APPROVED' });
    } catch {
      failed.push(id);
    }
  }
  selected.value = failed;
  approving.value = false;
}

function formatDateTime(d: Date): string {
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
</script>
